import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Text } from '../ui'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import type { SupportCenterFormKind } from '../../types/supportCenterSpecialties'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

type SimpleAttendanceNotesFormProps = {
  appointment: SupportCenterAgendaEvent
  formKind: Extract<
    SupportCenterFormKind,
    'nutricional' | 'fitness' | 'juridico' | 'social'
  >
  canSubmit: boolean
  label: string
  onClose: () => void
  onSubmitted: () => void
  /** Rodapé fixo do modal. Os botões de ação são renderizados nele. */
  actionsHost: HTMLElement | null
}

export const SimpleAttendanceNotesForm: React.FC<SimpleAttendanceNotesFormProps> = ({
  appointment,
  formKind,
  canSubmit,
  label,
  onClose,
  onSubmitted,
  actionsHost,
}) => {
  const readOnly = appointment.status === 'realizado' || !!appointment.attendance_completed_at
  const [notes, setNotes] = React.useState('')
  const [loadingNotes, setLoadingNotes] = React.useState(readOnly)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    setSaving(false)
    setError(null)
    setConfirmOpen(false)
    if (!readOnly) {
      setNotes('')
      setLoadingNotes(false)
      return
    }
    setLoadingNotes(true)
    void supportCenterAttendService
      .getAttendanceNotes(formKind, appointment.id)
      .then((text) => {
        if (!cancelled) setNotes(text ?? '')
      })
      .catch((e) => {
        if (!cancelled) {
          setNotes('')
          setError(e instanceof Error ? e.message : 'Não foi possível carregar o atendimento.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingNotes(false)
      })
    return () => {
      cancelled = true
    }
  }, [appointment.id, formKind, readOnly])

  const requestConfirm = () => {
    if (!canSubmit || saving) return
    if (!notes.trim()) {
      setError(`Informe ${label.charAt(0).toLowerCase()}${label.slice(1)}.`)
      return
    }
    setError(null)
    setConfirmOpen(true)
  }

  const handleSubmit = async () => {
    if (!canSubmit || saving) return
    setSaving(true)
    setError(null)
    try {
      await supportCenterAttendService.submitAttendanceNotes(formKind, appointment.id, notes)
      setConfirmOpen(false)
      onSubmitted()
    } catch (e) {
      setConfirmOpen(false)
      setError(e instanceof Error ? e.message : 'Não foi possível salvar o atendimento.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label} {!readOnly && <span className="text-red-500">*</span>}
        </label>
        <textarea
          rows={6}
          value={loadingNotes ? 'Carregando...' : notes}
          disabled={readOnly || !canSubmit || saving || loadingNotes}
          onChange={(e) => setNotes(e.target.value)}
          className={`${inputClass} resize-none`}
        />
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {actionsHost
        ? createPortal(
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                {readOnly ? 'Fechar' : 'Cancelar'}
              </Button>
              {canSubmit && !readOnly && (
                <Button onClick={requestConfirm} disabled={saving}>
                  {saving ? 'Salvando...' : 'Concluir atendimento'}
                </Button>
              )}
            </>,
            actionsHost
          )
        : null}

      {confirmOpen &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Text size="sm" color="muted">
                Ao confirmar, o atendimento será marcado como realizado. Deseja continuar?
              </Text>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button onClick={() => void handleSubmit()} disabled={saving}>
                  {saving ? 'Salvando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
